import {
	BankAccountStatus,
	type ShareholderInvestorPersonBankAccountsRes,
} from '@dealstack/shared/lib/generated';

const CONFIRMATION_PENDING_STATUSES = new Set<BankAccountStatus>([
	BankAccountStatus.DRAFT,
	BankAccountStatus.INVESTOR_OTP_REQUESTED,
	BankAccountStatus.INVESTOR_OTP_FAILED,
]);

// Whether a save left the account awaiting the emailed confirmation code. An unknown
// status falls back to requesting one — the backend rejects requests that make no sense.
export const isBankAccountConfirmationPending = (status?: BankAccountStatus): boolean =>
	status === undefined || CONFIRMATION_PENDING_STATUSES.has(status);

// Which lifecycle status an investor is shown on their own account. Only the state that
// asks something of them — a code is outstanding — earns a badge; the admin decision
// (pending review, approved, rejected) is not theirs to see. Shared so the profile and
// the joiner/leaver steps cannot drift apart on it.
export const shouldShowInvestorStatusBadge = (status?: BankAccountStatus): boolean =>
	status === BankAccountStatus.INVESTOR_OTP_REQUESTED;

// Mirrors CONFIRMED_BANK_ACCOUNT_STATUSES in the bank account lifecycle: the statuses the
// rest of the platform treats as a usable account. Draft and the code states are not
// usable because the investor has not confirmed the account; rejected and inactive ones
// are not usable because they are finished.
const USABLE_STATUSES = new Set<BankAccountStatus>([
	BankAccountStatus.PENDING_VERIFICATION,
	BankAccountStatus.PENDING_ADMIN_REVIEW,
	BankAccountStatus.APPROVED,
]);

export const isBankAccountUsable = (status?: BankAccountStatus): boolean =>
	status !== undefined && USABLE_STATUSES.has(status);

// A joiner or leaver step cannot continue until the investor's account is one the rest of
// the platform will accept. Without this the step passes with an unconfirmed account that
// no payment, document or export will use. Reads the account the step renders.
export const isContinueBlockedByBankAccount = (
	bankAccounts?: { status?: BankAccountStatus }[]
): boolean => {
	const [account] = bankAccounts ?? [];
	return !account || !isBankAccountUsable(account.status);
};

/**
 * Drops declined accounts from a joiner/leaver step's copy of the response. A declined
 * account can never be edited back into use, so the step offers a fresh form rather than
 * a tile whose Continue can never pass. The profile deliberately still lists everything.
 */
export const excludeRejectedBankAccounts = (
	data: ShareholderInvestorPersonBankAccountsRes[]
): ShareholderInvestorPersonBankAccountsRes[] =>
	data.map((entry) => ({
		...entry,
		bankAccounts: entry.bankAccounts.filter(
			(account) => account.status !== BankAccountStatus.REJECTED
		),
	}));
